const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENSION = '\f^{}\\[~]|€';

const GSM7_BASIC_SET = new Set(Array.from(GSM7_BASIC));
const GSM7_EXTENSION_SET = new Set(Array.from(GSM7_EXTENSION));

export type SmsEncoding = 'GSM-7' | 'UCS-2';

export interface SmsSegmentInfo {
  encoding: SmsEncoding;
  length: number;
  segments: number;
  perSegment: number;
}

const SEGMENT_LIMITS: Record<SmsEncoding, { single: number; multi: number }> = {
  'GSM-7': { single: 160, multi: 153 },
  'UCS-2': { single: 70, multi: 67 },
};

export const isGsm7 = (message: string): boolean =>
  Array.from(message).every((char) => GSM7_BASIC_SET.has(char) || GSM7_EXTENSION_SET.has(char));

// GSM-7 extension characters take two septets; UCS-2 characters outside the BMP take two code units.
const charWidth = (char: string, encoding: SmsEncoding): number =>
  encoding === 'GSM-7' ? (GSM7_EXTENSION_SET.has(char) ? 2 : 1) : char.length;

export const getSmsSegmentInfo = (message: string): SmsSegmentInfo => {
  const encoding: SmsEncoding = isGsm7(message) ? 'GSM-7' : 'UCS-2';
  const { single, multi } = SEGMENT_LIMITS[encoding];
  const widths = Array.from(message).map((char) => charWidth(char, encoding));
  const length = widths.reduce((sum, width) => sum + width, 0);

  if (length === 0) return { encoding, length, segments: 0, perSegment: single };
  if (length <= single) return { encoding, length, segments: 1, perSegment: single };

  // Multi-byte characters are never split across segments, so fill greedily.
  let segments = 1;
  let used = 0;
  widths.forEach((width) => {
    if (used + width > multi) {
      segments += 1;
      used = 0;
    }
    used += width;
  });

  return { encoding, length, segments, perSegment: multi };
};

export const getMessageUnits = (message: string): number => getSmsSegmentInfo(message).segments;
